import { useCallback, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import notasD2lService from '../services/notasD2lService';
import { notasD2lKeys } from '../queryKeys';
import { PAGE_SIZE } from '../constants';

const normalizar = (res) => {
  const total = Number(res?.total);
  return {
    rows: Array.isArray(res?.rows) ? res.rows : [],
    total: Number.isFinite(total) && total > 0 ? total : 0,
  };
};

// Una página vacía corta la paginación aunque total diga que faltan filas, para
// no pedir páginas en bucle si el total del backend no cuadra.
export const siguientePagina = (ultimaPagina, paginas) => {
  const cargadas = paginas.reduce((n, p) => n + p.rows.length, 0);
  if (ultimaPagina.rows.length === 0 || cargadas >= ultimaPagina.total) return undefined;
  return paginas.length + 1;
};

const useNotasD2l = ({ headquarter, filtros }) => {
  const query = useInfiniteQuery({
    queryKey: notasD2lKeys.lista(headquarter, filtros),
    queryFn: async ({ pageParam }) => {
      const res = await notasD2lService.obtenerNotas({
        headquarter,
        ...filtros,
        page: pageParam,
        pageSize: PAGE_SIZE,
      });
      return normalizar(res);
    },
    initialPageParam: 1,
    getNextPageParam: siguientePagina,
    enabled: Boolean(headquarter) && filtros !== null,
    staleTime: 60 * 1000,
  });

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const paginas = data?.pages;

  const rows = useMemo(() => (paginas ? paginas.flatMap((p) => p.rows) : []), [paginas]);
  const total = paginas?.length ? paginas[paginas.length - 1].total : 0;

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    rows,
    total,
    hasMore: Boolean(hasNextPage),
    loadMore,
    isLoading: query.isLoading,
    isFetching: query.isFetching && !isFetchingNextPage,
    isFetchingNextPage,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

export default useNotasD2l;
